/**
 * PATH: packages/ui/src/diagnostics/useRenderTiming.ts
 * PURPOSE: A React hook that measures how long a named UI element takes to
 *          render-to-paint (in ms) and records it as a 'render' perf
 *          sample, so the 0x3admin Performance dashboard can show
 *          "render p95 per email element" across tenants.
 *
 * USAGE:
 *   function EmailList() {
 *     useRenderTiming('EmailList')
 *     ...
 *   }
 *   // Re-measures when a key input changes (e.g. the open email):
 *   useRenderTiming('EmailReader', { deps: [emailId] })
 *
 * HOW IT MEASURES:
 *   Captures a timestamp during render, then reads the clock inside a
 *   requestAnimationFrame scheduled from an effect — i.e. after React has
 *   committed and the browser is about to paint. The delta approximates
 *   the user-perceived render cost of this element. Sampling + recording
 *   are delegated to the shared perf reporter (no-op until it's installed).
 */

import { useEffect } from 'react'

import { _passSample, recordPerfSample } from './clientPerfReporter'

export interface UseRenderTimingOptions {
  /** Disable measurement (e.g. while a placeholder/skeleton is shown). */
  enabled?: boolean
  /** Re-measure whenever one of these values changes. Default: mount only. */
  deps?: ReadonlyArray<unknown>
  /** Extra context stored alongside the sample. */
  extra?: Record<string, unknown>
}

const _now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()

export function useRenderTiming(name: string, options: UseRenderTimingOptions = {}): void {
  const { enabled = true, deps = [], extra } = options
  // Per-render timestamp — captured during render of THIS commit.
  const start = _now()

  useEffect(() => {
    if (!enabled) return
    if (typeof window === 'undefined') return
    if (!_passSample()) return

    const schedule =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (cb: FrameRequestCallback) => window.setTimeout(() => cb(_now()), 0)
    const cancel =
      typeof cancelAnimationFrame === 'function'
        ? cancelAnimationFrame
        : (id: number) => window.clearTimeout(id)

    const handle = schedule(() => {
      recordPerfSample({
        kind: 'render',
        name,
        duration_ms: Math.round(_now() - start),
        route: `${window.location.pathname}${window.location.hash}`,
        extra,
      })
    })
    return () => cancel(handle as number)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, enabled, ...deps])
}
